import { useEffect, useState } from "react";
import { apiStaff, apiTech } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, FileText, ExternalLink, Mail, MessageSquare,
  Eye, Receipt, Loader2, BadgeDollarSign, CheckCircle2, X,
} from "lucide-react";

const TAX_RATE_BEXAR = 0.0825;
const CARD_FEE_DEFAULT = 0.03;

const KIND_TONES = {
  labor: "bg-[#FF6600]/10 text-[#FF6600] border-[#FF6600]",
  parts: "bg-[#FF6600]/10 text-[#FF6600] border-[#FF6600]",
  fee: "bg-[#FFFFFF] text-[#002060] border-[#002060]/15",
  other: "bg-[#FFFFFF] text-[#002060] border-[#002060]/15",
};

/**
 * In-page Invoice manager — lists existing invoices for a job, opens the
 * builder inline, posts to /crm/invoices. Works for both manager and tech
 * roles (backend authorizes assigned tech for the job).
 *
 * Props:
 *  - job: { id, customer_name, customer_email, customer_phone, address,
 *           appliance, quoted_price, final_price, appliance_make, appliance_model }
 *  - role: "manager" | "tech"
 *  - onChanged: optional callback after invoice create/send
 *  - onView: callback(invoice) when user taps "View / PDF" on a row — parent
 *            can route to the branded preview. If absent, opens pay_url in new tab.
 */
export default function JobInvoicePanel({ job, role = "manager", onChanged, onView }) {
  const api = role === "tech" ? apiTech : apiStaff;
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);

  async function fetchInvoices() {
    if (!job?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/crm/jobs/${job.id}/invoices`);
      setInvoices(r.data);
    } catch (_e) { /* ignore */ }
    setLoading(false);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchInvoices(); }, [job?.id]);

  function handleCreated(inv) {
    setBuilderOpen(false);
    setInvoices((arr) => [inv, ...arr]);
    if (onChanged) onChanged(inv);
  }

  const reload = fetchInvoices;

  return (
    <section
      data-testid="job-invoice-panel"
      className="bg-[#002060] text-[#FFFFFF] rounded-lg border-2 border-[#FF6600]/30 overflow-hidden"
    >
      <header className="px-4 py-3 bg-[#002060]/85 border-b border-[#FF6600]/20 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <Receipt className="w-4 h-4 text-[#FF6600] shrink-0" />
          <div>
            <div className="label-retro text-[10px] uppercase tracking-[0.25em] text-[#FF6600]">Invoices</div>
            <div className="text-[#FFFFFF]/70 text-[11px]">
              {loading ? "Loading…" : `${invoices.length} for this job`}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setBuilderOpen((v) => !v)}
          data-testid="invoice-toggle-builder"
          className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-[#FFFFFF] font-semibold uppercase tracking-widest text-[10px] px-3 h-8 rounded transition-colors inline-flex items-center gap-1.5"
        >
          {builderOpen ? "Close" : <><Plus className="w-3.5 h-3.5" /> New invoice</>}
        </button>
      </header>

      {/* Existing invoices list */}
      {invoices.length > 0 && (
        <ul className="divide-y divide-[#FFFFFF]/10">
          {invoices.map((inv) => (
            <InvoiceRow
              key={inv.id}
              inv={inv}
              api={api}
              onView={onView}
              onChanged={reload}
            />
          ))}
        </ul>
      )}

      {invoices.length === 0 && !loading && !builderOpen && (
        <div className="px-4 py-6 text-center text-[#FFFFFF]/70 text-sm">
          No invoices yet. Tap <b className="text-[#FF6600]">New invoice</b> to build one.
        </div>
      )}

      {/* Builder */}
      {builderOpen && (
        <InvoiceBuilder
          job={job}
          api={api}
          onCancel={() => setBuilderOpen(false)}
          onCreated={handleCreated}
        />
      )}
    </section>
  );
}

// ============== Invoice row (collapsed) ==============
function InvoiceRow({ inv, api, onView, onChanged }) {
  const [sending, setSending] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);

  async function emailIt() {
    setSending(true);
    try {
      await api.post(`/crm/invoices/${inv.id}/send`);
      toast.success(`Emailed to ${inv.customer_email}`);
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  }

  async function smsIt() {
    try {
      const r = await api.post(`/crm/invoices/${inv.id}/send-link`);
      const sms = r.data.sms;
      if (!sms) return toast.error("No customer phone on file");
      window.location.href = sms;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  const statusTone = {
    paid: "bg-[#FF6600] text-[#FFFFFF] border-[#FF6600]",
    partially_paid: "bg-[#FFFFFF] text-[#FF6600] border-[#FF6600]",
    sent: "bg-[#FFFFFF] text-[#FF6600] border-[#FF6600]",
    void: "bg-[#FFFFFF]/10 text-[#FFFFFF]/50 border-[#FFFFFF]/20 line-through",
    draft: "bg-[#FFFFFF]/10 text-[#FFFFFF] border-[#FFFFFF]/40",
  }[inv.status] || "bg-[#FFFFFF]/10 text-[#FFFFFF]/70 border-[#FFFFFF]/20";

  const canMarkPaid = inv.status !== "paid" && inv.status !== "void";
  const balance = Math.max(0, Number(inv.balance_due ?? inv.total) - 0);
  const paid = Number(inv.amount_paid || 0);
  const showPartial = paid > 0 && inv.status !== "paid";

  return (
    <>
      <li className="px-4 py-3 flex items-start justify-between gap-3 hover:bg-[#FFFFFF]/10 transition-colors">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="num text-[#FFFFFF] font-semibold text-sm">{inv.invoice_number}</span>
            <span className={`inline-block text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${statusTone}`}>
              {(inv.status || "").replace("_", " ")}
            </span>
            {inv.payment_type === "split" && (
              <span className="inline-block text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-[#FF6600] text-[#FF6600]">
                Split
              </span>
            )}
          </div>
          <div className="text-[#FFFFFF]/50 text-[11px] mt-0.5 num">
            {new Date(inv.created_at).toLocaleDateString()}{inv.paid_at && ` · paid ${new Date(inv.paid_at).toLocaleDateString()}`}
            {showPartial && <> · paid <b className="text-[#FF6600]">${paid.toFixed(2)}</b> / ${Number(inv.total).toFixed(2)}</>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="num text-[#FF6600] font-semibold text-base">
            {showPartial ? `$${balance.toFixed(2)}` : `$${Number(inv.total).toFixed(2)}`}
          </span>
          <button
            type="button"
            onClick={() => onView ? onView(inv) : window.open(inv.pay_url, "_blank")}
            data-testid={`inv-view-${inv.id}`}
            className="ml-1 w-8 h-8 grid place-items-center rounded text-[#FFFFFF]/70 hover:text-[#FF6600] hover:bg-[#FFFFFF]/10 transition"
            title="View / PDF"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          <a
            href={inv.pay_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`inv-link-${inv.id}`}
            className="w-8 h-8 grid place-items-center rounded text-[#FFFFFF]/70 hover:text-[#FF6600] hover:bg-[#FFFFFF]/10 transition"
            title="Open pay-link"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
          {canMarkPaid && (
            <button
              type="button"
              onClick={() => setMarkPaidOpen(true)}
              data-testid={`inv-markpaid-${inv.id}`}
              className="w-8 h-8 grid place-items-center rounded text-[#FFFFFF]/70 hover:text-[#FF6600] hover:bg-[#FFFFFF]/10 transition"
              title="Mark as paid (cash / check / Zelle…)"
            >
              <BadgeDollarSign className="w-3.5 h-3.5" />
            </button>
          )}
          {inv.status !== "paid" && inv.status !== "void" && inv.customer_email && (
            <button
              type="button"
              onClick={emailIt}
              disabled={sending}
              data-testid={`inv-email-${inv.id}`}
              className="w-8 h-8 grid place-items-center rounded text-[#FFFFFF]/70 hover:text-[#FF6600] hover:bg-[#FFFFFF]/10 transition disabled:opacity-50"
              title="Email pay-link"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            </button>
          )}
          {inv.status !== "paid" && inv.status !== "void" && inv.customer_phone && (
            <button
              type="button"
              onClick={smsIt}
              data-testid={`inv-sms-${inv.id}`}
              className="w-8 h-8 grid place-items-center rounded text-[#FFFFFF]/70 hover:text-[#FF6600] hover:bg-[#FFFFFF]/10 transition"
              title="Send via SMS"
            >
              <MessageSquare className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </li>
      {markPaidOpen && (
        <MarkAsPaidModal
          inv={inv}
          api={api}
          onClose={() => setMarkPaidOpen(false)}
          onPaid={() => { setMarkPaidOpen(false); onChanged && onChanged(); }}
        />
      )}
    </>
  );
}

// ============== Mark As Paid modal ==============
const PAY_METHODS = [
  { key: "cash", label: "Cash" },
  { key: "check", label: "Check" },
  { key: "zelle", label: "Zelle" },
  { key: "cash_app", label: "Cash App" },
  { key: "venmo", label: "Venmo" },
  { key: "card_manual", label: "Card (manual)" },
];

function MarkAsPaidModal({ inv, api, onClose, onPaid }) {
  const total = Number(inv.total) || 0;
  const paid = Number(inv.amount_paid || 0);
  const balance = Math.max(0, total - paid);
  const isSplit = inv.payment_type === "split";
  const depositRequired = Number(inv.deposit_amount_required || 0);
  // What's still owed as a "deposit" (only relevant if invoice was created as split).
  const depositOwed = isSplit && paid === 0 ? depositRequired : 0;
  // If the invoice is "full" but the tech is collecting a partial cash deposit ad-hoc,
  // we still let them pick "deposit" allocation — it just means a partial payment.
  const depositAllowed = paid === 0; // any first payment can be a deposit

  // Default allocation: deposit if split-invoice + nothing paid yet; balance if a deposit already exists; full otherwise.
  const initialAllocation = depositOwed > 0 ? "deposit" : (paid > 0 ? "balance" : "full");
  const [allocation, setAllocation] = useState(initialAllocation);
  // Default amount: deposit suggests the configured deposit if split, otherwise half (editable).
  const suggestedDeposit = depositOwed > 0 ? depositOwed : Math.round((total / 2) * 100) / 100;
  const initialAmount = allocation === "deposit" ? suggestedDeposit : balance;
  const [amount, setAmount] = useState(String(initialAmount.toFixed(2)));
  const [method, setMethod] = useState("cash");
  const [checkNumber, setCheckNumber] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  function pickAllocation(k) {
    setAllocation(k);
    if (k === "deposit") setAmount(String(suggestedDeposit.toFixed(2)));
    else if (k === "balance" || k === "full") setAmount(String(balance.toFixed(2)));
  }

  async function submit() {
    const amt = Number(amount) || 0;
    if (amt <= 0) return toast.error("Amount must be > 0");
    if (amt > balance + 0.01) return toast.error(`Amount can't exceed remaining balance $${balance.toFixed(2)}`);
    if (method === "check" && !checkNumber.trim()) return toast.error("Check number is required");
    setBusy(true);
    try {
      const resp = await api.post(`/crm/invoices/${inv.id}/payments`, {
        invoice_id: inv.id,
        amount: amt,
        payment_method: method,
        payment_applied_to: allocation,
        check_number: method === "check" ? checkNumber.trim() : null,
        note: note.trim() || null,
      });
      const flipped = resp.data?.job_status_changed;
      const methodLabel = method.replace("_", " ");
      const allocLabel = allocation === "deposit" ? "deposit" : allocation === "balance" ? "balance" : "payment";
      if (flipped === "part-ordered") {
        toast.success(`$${amt.toFixed(2)} ${allocLabel} recorded — job moved to Part Ordered`);
      } else {
        toast.success(`$${amt.toFixed(2)} ${methodLabel} ${allocLabel} recorded`);
      }
      onPaid();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 grid place-items-center p-3"
      onClick={onClose}
      data-testid="mark-paid-modal"
    >
      <div
        className="bg-[#FFFFFF] text-[#002060] rounded-xl max-w-md w-full max-h-[92vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-3 border-b-2 border-[#FF6600] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FF6600] font-bold">Mark as paid</div>
            <div className="font-semibold text-[#002060] num">{inv.invoice_number} · ${total.toFixed(2)}</div>
          </div>
          <button onClick={onClose} className="text-[#002060]/40 hover:text-[#FF6600] transition" data-testid="mark-paid-close">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Balance summary */}
          <div className="bg-[#002060]/5 border border-[#002060]/15 rounded-md p-3 text-sm space-y-0.5">
            <div className="flex justify-between"><span className="text-[#002060]/70">Total</span><b className="num">${total.toFixed(2)}</b></div>
            {paid > 0 && (
              <div className="flex justify-between"><span className="text-[#002060]/70">Already paid</span><b className="num text-[#FF6600]">${paid.toFixed(2)}</b></div>
            )}
            <div className="flex justify-between border-t border-[#002060]/15 mt-1 pt-1">
              <span className="text-[#002060]/70 font-semibold">Remaining</span>
              <b className="num text-[#FF6600] text-base" data-testid="mark-paid-remaining">${balance.toFixed(2)}</b>
            </div>
            {isSplit && depositOwed > 0 && (
              <div className="text-[10px] uppercase tracking-widest text-[#FF6600] font-bold pt-1.5 leading-tight">
                Split invoice — deposit owed ${depositOwed.toFixed(2)}
              </div>
            )}
          </div>

          {/* Step 1: Allocation */}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#002060]/70 mb-2">Step 1 — Applied to</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: "deposit", label: "Deposit", disabled: !depositAllowed },
                { k: "balance", label: "Balance", disabled: paid === 0 },
                { k: "full",    label: "Full payment", disabled: paid > 0 },
              ].map(({ k, label, disabled }) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => !disabled && pickAllocation(k)}
                  disabled={disabled}
                  data-testid={`mark-paid-alloc-${k}`}
                  className={`h-12 rounded-md border-2 text-xs font-semibold uppercase tracking-wide transition ${
                    allocation === k && !disabled
                      ? "border-[#FF6600] bg-[#FF6600] text-[#FFFFFF]"
                      : disabled
                        ? "border-[#002060]/10 bg-[#002060]/5 text-[#002060]/30 cursor-not-allowed"
                        : "border-[#002060]/20 bg-[#FFFFFF] text-[#002060] hover:border-[#002060]/50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Amount */}
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#002060]/70">Amount ($)</label>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="mark-paid-amount"
              className="mt-1 w-full h-14 px-3 text-right text-2xl num font-bold rounded-md border-2 border-[#002060]/20 focus:border-[#FF6600] focus:outline-none focus:ring-2 focus:ring-[#FF6600]/30 text-[#002060]"
            />
            {/* Live "after this payment" preview */}
            {Number(amount) > 0 && Number(amount) <= balance + 0.01 && (
              <div
                className="mt-2 bg-[#FF6600]/10 border border-[#FF6600]/40 rounded-md px-3 py-2 text-xs text-[#002060] leading-snug"
                data-testid="mark-paid-preview"
              >
                After this payment: <b className="num text-[#FF6600]">${(paid + Number(amount)).toFixed(2)}</b> paid of{" "}
                <b className="num">${total.toFixed(2)}</b> ·{" "}
                <b className="num">${Math.max(balance - Number(amount), 0).toFixed(2)}</b> remaining
                {allocation === "deposit" && isSplit && (
                  <span className="block mt-1 text-[#FF6600] font-semibold uppercase tracking-wider text-[10px]">
                    → Job will move to Part Ordered
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Step 2: Method */}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#002060]/70 mb-2">Step 2 — Payment method</div>
            <div className="grid grid-cols-3 gap-2">
              {PAY_METHODS.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMethod(key)}
                  data-testid={`mark-paid-method-${key}`}
                  className={`h-11 rounded-md border-2 text-xs font-semibold transition ${
                    method === key
                      ? "border-[#FF6600] bg-[#FF6600] text-[#FFFFFF]"
                      : "border-[#002060]/20 bg-[#FFFFFF] text-[#002060] hover:border-[#002060]/50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Conditional: check number */}
          {method === "check" && (
            <div data-testid="mark-paid-check-number-row">
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#002060]/70">Check number *</label>
              <input
                type="text"
                value={checkNumber}
                onChange={(e) => setCheckNumber(e.target.value)}
                data-testid="mark-paid-check-number"
                placeholder="e.g. 1542"
                className="mt-1 w-full h-11 px-3 num rounded-md border border-[#002060]/20 focus:border-[#FF6600] focus:outline-none focus:ring-2 focus:ring-[#FF6600]/30"
                autoFocus
              />
            </div>
          )}

          {/* Optional note */}
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#002060]/70">Note (optional)</label>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="mark-paid-note"
              placeholder="Memo / reference…"
              className="mt-1 w-full text-sm rounded-md border border-[#002060]/20 focus:border-[#FF6600] focus:outline-none focus:ring-2 focus:ring-[#FF6600]/30 p-2"
            />
          </div>
        </div>
        <footer className="p-4 border-t border-[#002060]/10 grid grid-cols-2 gap-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            data-testid="mark-paid-cancel"
            className="h-11 rounded-md border-2 border-[#002060]/20 text-[#002060] font-semibold hover:bg-[#002060]/5 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="mark-paid-submit"
            className="h-11 rounded-md bg-[#FF6600] text-[#FFFFFF] font-bold uppercase tracking-widest text-xs hover:bg-[#FF6600]/90 transition disabled:opacity-50"
          >
            {busy ? "Saving…" : <><CheckCircle2 className="w-4 h-4 inline mr-1.5 -mt-0.5" />Record payment</>}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ============== Invoice builder (inline form) ==============
function InvoiceBuilder({ job, api, onCancel, onCreated }) {
  const [items, setItems] = useState(() => seedItems(job));
  const [applianceMake, setApplianceMake] = useState(job?.appliance_make || "");
  const [applianceModel, setApplianceModel] = useState(job?.appliance_model || "");
  const [serviceFee, setServiceFee] = useState("");
  const [discount, setDiscount] = useState("");
  const [diagnosticWaived, setDiagnosticWaived] = useState(false);
  const [applyTax, setApplyTax] = useState(true);
  const [taxRate, setTaxRate] = useState(TAX_RATE_BEXAR);
  const [applyCardFee, setApplyCardFee] = useState(false);
  const [cardFeeRate, setCardFeeRate] = useState(CARD_FEE_DEFAULT);
  const [paymentTerms, setPaymentTerms] = useState("Due on receipt");
  const [notes, setNotes] = useState("");
  const [sendMode, setSendMode] = useState("none"); // none | email | sms
  const [busy, setBusy] = useState(false);
  // Split-payment state
  const isPartOrdered = job?.status === "part-ordered";
  const [paymentType, setPaymentType] = useState(isPartOrdered ? "split" : "full");
  const [depositInput, setDepositInput] = useState(""); // empty = use 50% default

  function addItem(preset = null) {
    setItems((arr) => [...arr, preset || { description: "", qty: 1, unit_price: "", kind: "labor" }]);
  }
  function updateItem(i, patch) {
    setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  }
  function removeItem(i) {
    setItems((arr) => arr.filter((_, idx) => idx !== i));
  }

  // Live totals
  const itemsSubtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const sfNum = Number(serviceFee) || 0;
  const dNum = Number(discount) || 0;
  const beforeTax = Math.max(0, itemsSubtotal + sfNum - dNum);
  const effectiveTaxRate = applyTax ? (Number(taxRate) || 0) : 0;
  const effectiveCardRate = applyCardFee ? (Number(cardFeeRate) || 0) : 0;
  const taxAmt = beforeTax * effectiveTaxRate;
  const preCard = beforeTax + taxAmt;
  const cardFeeAmt = preCard * effectiveCardRate;
  const total = preCard + cardFeeAmt;

  // Computed deposit value (lives outside JSX so the submit can include it)
  const defaultDeposit = +(total * 0.5).toFixed(2);
  const depositAmount = depositInput === "" ? defaultDeposit : Math.min(Math.max(Number(depositInput) || 0, 0), total);

  async function submit() {
    const clean = items
      .map((it) => ({
        description: (it.description || "").trim(),
        qty: Number(it.qty) || 0,
        unit_price: Number(it.unit_price) || 0,
        kind: it.kind || "other",
      }))
      .filter((it) => it.description && it.qty > 0 && it.unit_price >= 0);
    if (!clean.length) return toast.error("Add at least one line item");
    if (total <= 0) return toast.error("Total must be greater than zero");
    if (paymentType === "split" && (depositAmount <= 0 || depositAmount >= total)) {
      return toast.error("Deposit must be between $0.01 and the total");
    }

    setBusy(true);
    try {
      const r = await api.post(
        "/crm/invoices",
        {
          job_id: job.id,
          line_items: clean,
          appliance_make: applianceMake.trim() || null,
          appliance_model: applianceModel.trim() || null,
          service_fee: sfNum,
          discount: dNum,
          diagnostic_fee_waived: diagnosticWaived,
          tax_rate: effectiveTaxRate,
          card_fee_rate: effectiveCardRate,
          payment_terms: paymentTerms.trim() || "Due on receipt",
          notes: notes.trim() || null,
          payment_type: paymentType,
          deposit_amount_required: paymentType === "split" ? depositAmount : null,
        },
        { headers: { "X-Public-Origin": window.location.origin } },
      );
      const created = r.data;
      toast.success(`Invoice ${created.invoice_number} saved`);

      // Optional send
      if (sendMode === "email" && job.customer_email) {
        try {
          await api.post(`/crm/invoices/${created.id}/send`);
          toast.success(`Emailed to ${job.customer_email}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Couldn't email"); }
      } else if (sendMode === "sms" && job.customer_phone) {
        try {
          const r2 = await api.post(`/crm/invoices/${created.id}/send-link`);
          if (r2.data.sms) window.location.href = r2.data.sms;
        } catch (e) { toast.error(e.response?.data?.detail || "Couldn't open SMS"); }
      }

      onCreated && onCreated(created);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="bg-[#FFFFFF] text-[#002060] p-4 space-y-4">
      {/* Appliance */}
      <Sec title="Appliance info">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Make">
            <Input value={applianceMake} onChange={(e) => setApplianceMake(e.target.value)} placeholder="Samsung, LG…" testid="builder-make" />
          </Field>
          <Field label="Model">
            <Input value={applianceModel} onChange={(e) => setApplianceModel(e.target.value)} placeholder="RF28R…" testid="builder-model" />
          </Field>
        </div>
      </Sec>

      {/* Line items */}
      <Sec title="Labor · parts · fees" data-testid="builder-items">
        <div className="space-y-1.5">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
              <input
                className="col-span-5 h-9 px-2 text-sm rounded bg-[#FFFFFF] border border-[#002060]/20 focus:border-[#FF6600] focus:outline-none"
                placeholder="Description"
                value={it.description}
                onChange={(e) => updateItem(i, { description: e.target.value })}
                data-testid={`builder-desc-${i}`}
              />
              <input
                type="number" inputMode="decimal" min="0" step="0.5"
                className="col-span-1 h-9 px-2 text-sm text-right num rounded bg-[#FFFFFF] border border-[#002060]/20 focus:border-[#FF6600] focus:outline-none"
                placeholder="Qty"
                value={it.qty}
                onChange={(e) => updateItem(i, { qty: e.target.value })}
                data-testid={`builder-qty-${i}`}
              />
              <input
                type="number" inputMode="decimal" min="0" step="0.01"
                className="col-span-2 h-9 px-2 text-sm text-right num rounded bg-[#FFFFFF] border border-[#002060]/20 focus:border-[#FF6600] focus:outline-none"
                placeholder="$ Unit"
                value={it.unit_price}
                onChange={(e) => updateItem(i, { unit_price: e.target.value })}
                data-testid={`builder-price-${i}`}
              />
              <select
                className={`col-span-2 h-9 px-2 text-xs rounded border focus:border-[#FF6600] focus:outline-none uppercase tracking-wide ${KIND_TONES[it.kind || "labor"]}`}
                value={it.kind || "labor"}
                onChange={(e) => updateItem(i, { kind: e.target.value })}
                data-testid={`builder-kind-${i}`}
              >
                <option value="labor">Labor</option>
                <option value="parts">Parts</option>
                <option value="fee">Fee</option>
                <option value="other">Other</option>
              </select>
              <div className="col-span-1 text-right text-sm num font-semibold">
                ${((Number(it.qty) || 0) * (Number(it.unit_price) || 0)).toFixed(2)}
              </div>
              <button
                type="button"
                onClick={() => removeItem(i)}
                disabled={items.length === 1}
                data-testid={`builder-del-${i}`}
                className="col-span-1 h-9 grid place-items-center text-[#002060]/40 hover:text-[#FF6600] disabled:opacity-30 transition"
                aria-label="Remove line"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          <Chip onClick={() => addItem({ description: "Diagnostic visit", qty: 1, unit_price: 89, kind: "labor" })} testid="builder-add-diag">+ Diagnostic</Chip>
          <Chip onClick={() => addItem({ description: "Labor — repair", qty: 1, unit_price: 145, kind: "labor" })} testid="builder-add-labor">+ Labor</Chip>
          <Chip onClick={() => addItem({ description: "Parts", qty: 1, unit_price: 0, kind: "parts" })} testid="builder-add-parts">+ Parts</Chip>
          <Chip onClick={() => addItem({ description: "Trip / service fee", qty: 1, unit_price: 25, kind: "fee" })} testid="builder-add-fee">+ Fee</Chip>
          <Chip onClick={() => addItem()} testid="builder-add-blank"><Plus className="w-3 h-3 inline" /> Blank</Chip>
        </div>
      </Sec>

      {/* Adjustments */}
      <Sec title="Adjustments">
        <div className="grid grid-cols-2 gap-2 mb-2">
          <Field label="Service fee ($)">
            <Input type="number" inputMode="decimal" step="0.01" min="0" value={serviceFee} onChange={(e) => setServiceFee(e.target.value)} placeholder="0" testid="builder-service-fee" align="right" />
          </Field>
          <Field label="Discount ($)">
            <Input type="number" inputMode="decimal" step="0.01" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0" testid="builder-discount" align="right" />
          </Field>
        </div>
        <Toggle
          checked={diagnosticWaived}
          onChange={setDiagnosticWaived}
          testid="builder-diag-waived"
          label="Show diagnostic-fee-waived courtesy line"
        />
        <Toggle
          checked={applyTax}
          onChange={setApplyTax}
          testid="builder-apply-tax"
          label={`Apply sales tax`}
          tail={
            <input
              type="number" step="0.0001" min="0"
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
              data-testid="builder-tax-rate"
              className="ml-2 w-20 h-7 text-right text-xs num rounded bg-[#FFFFFF] border border-[#002060]/20 focus:border-[#FF6600] focus:outline-none px-2"
            />
          }
          suffix={<span className="ml-1 text-xs text-[#002060]/60 num">{(effectiveTaxRate * 100).toFixed(2)}%</span>}
        />
        <Toggle
          checked={applyCardFee}
          onChange={setApplyCardFee}
          testid="builder-apply-cardfee"
          label="Apply card processing fee"
          tail={
            <input
              type="number" step="0.001" min="0"
              value={cardFeeRate}
              onChange={(e) => setCardFeeRate(e.target.value)}
              data-testid="builder-cardfee-rate"
              className="ml-2 w-20 h-7 text-right text-xs num rounded bg-[#FFFFFF] border border-[#002060]/20 focus:border-[#FF6600] focus:outline-none px-2"
            />
          }
          suffix={<span className="ml-1 text-xs text-[#002060]/60 num">{(effectiveCardRate * 100).toFixed(2)}%</span>}
        />
      </Sec>

      {/* Terms + notes */}
      <Sec title="Terms &amp; notes">
        <Field label="Payment terms">
          <Input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} placeholder="Due on receipt" testid="builder-terms" />
        </Field>
        <Field label="Service notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="What was repaired, recommendations…"
            data-testid="builder-notes"
            className="mt-1 w-full text-sm rounded bg-[#FFFFFF] border border-[#002060]/20 focus:border-[#FF6600] focus:outline-none p-2"
          />
        </Field>
      </Sec>

      {/* Live totals */}
      <div className="bg-[#002060] text-[#FFFFFF] rounded-lg p-4 space-y-1 text-sm border-2 border-[#FF6600]/40">
        <TotalRow k="Subtotal" v={itemsSubtotal} />
        {sfNum > 0 && <TotalRow k="Service fee" v={sfNum} />}
        {dNum > 0 && <TotalRow k="Discount" v={-dNum} tone="emerald" />}
        {effectiveTaxRate > 0 && <TotalRow k={`Tax (${(effectiveTaxRate * 100).toFixed(2)}%)`} v={taxAmt} />}
        {effectiveCardRate > 0 && <TotalRow k={`Card fee (${(effectiveCardRate * 100).toFixed(2)}%)`} v={cardFeeAmt} />}
        <div className="flex items-center justify-between pt-2 mt-1 border-t border-[#FFFFFF]/20">
          <span className="label-retro text-[10px] uppercase tracking-[0.3em] text-[#FF6600]">Total owed</span>
          <span className="display-retro num text-[#FF6600] text-2xl" data-testid="builder-total">${total.toFixed(2)}</span>
        </div>
      </div>

      {/* Payment options: split vs full */}
      <Sec title="Payment options" data-testid="builder-payment-options">
        {isPartOrdered && paymentType === "full" && (
          <div className="text-[11px] text-[#FF6600] mb-2 -mt-1 font-semibold">
            ⚠ This job is waiting for a part — consider collecting a deposit.
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setPaymentType("full")}
            data-testid="builder-pt-full"
            className={`h-11 rounded-md border-2 text-sm font-semibold transition ${
              paymentType === "full"
                ? "border-[#FF6600] bg-[#FF6600] text-[#FFFFFF]"
                : "border-[#002060]/20 bg-[#FFFFFF] text-[#002060] hover:border-[#002060]/50"
            }`}
          >
            Collect full payment
          </button>
          <button
            type="button"
            onClick={() => setPaymentType("split")}
            data-testid="builder-pt-split"
            className={`h-11 rounded-md border-2 text-sm font-semibold transition ${
              paymentType === "split"
                ? "border-[#FF6600] bg-[#FF6600] text-[#FFFFFF]"
                : "border-[#002060]/20 bg-[#FFFFFF] text-[#002060] hover:border-[#002060]/50"
            }`}
          >
            Collect deposit
          </button>
        </div>
        {paymentType === "split" && (
          <div className="mt-3 grid grid-cols-2 gap-3 items-end">
            <Field label="Deposit ($)">
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                placeholder={`${defaultDeposit.toFixed(2)} (50% default)`}
                value={depositInput}
                onChange={(e) => setDepositInput(e.target.value)}
                testid="builder-deposit-amount"
                align="right"
              />
            </Field>
            <div className="text-xs text-[#002060]/80">
              <div>
                <span className="text-[#002060]/60">Customer pays now:</span>{" "}
                <b className="num text-[#FF6600]">${depositAmount.toFixed(2)}</b>
              </div>
              <div className="mt-0.5">
                <span className="text-[#002060]/60">Balance on completion:</span>{" "}
                <b className="num">${Math.max(total - depositAmount, 0).toFixed(2)}</b>
              </div>
            </div>
          </div>
        )}
      </Sec>

      {/* Send mode */}
      <Sec title="On save, also…">
        <div className="grid grid-cols-3 gap-1.5">
          <SendChip active={sendMode === "none"} onClick={() => setSendMode("none")} testid="builder-send-none">
            Save only
          </SendChip>
          <SendChip
            active={sendMode === "email"}
            disabled={!job.customer_email}
            onClick={() => setSendMode("email")}
            testid="builder-send-email"
          >
            <Mail className="w-3 h-3 mr-1 inline" /> Email
          </SendChip>
          <SendChip
            active={sendMode === "sms"}
            disabled={!job.customer_phone}
            onClick={() => setSendMode("sms")}
            testid="builder-send-sms"
          >
            <MessageSquare className="w-3 h-3 mr-1 inline" /> SMS
          </SendChip>
        </div>
        {sendMode === "email" && job.customer_email && (
          <div className="text-[11px] text-[#002060]/60 mt-1.5">→ Sends pay-link to <b>{job.customer_email}</b></div>
        )}
        {sendMode === "sms" && job.customer_phone && (
          <div className="text-[11px] text-[#002060]/60 mt-1.5">→ Opens your SMS app with pay-link for <b>{job.customer_phone}</b></div>
        )}
      </Sec>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          data-testid="builder-cancel"
          className="flex-1 h-11 border border-[#002060]/30 text-[#002060]/70 hover:bg-[#002060]/5 font-medium rounded transition text-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid="builder-save"
          className="flex-1 h-11 bg-[#FF6600] hover:bg-[#FF6600]/90 disabled:opacity-50 text-[#FFFFFF] font-bold uppercase tracking-widest text-xs rounded transition inline-flex items-center justify-center gap-2"
        >
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          <FileText className="w-3.5 h-3.5" /> Save invoice
        </button>
      </div>
    </div>
  );
}

// ============== Builder primitives ==============
function Sec({ title, children, ...rest }) {
  return (
    <section className="space-y-1.5" {...rest}>
      <div className="label-retro text-[10px] uppercase tracking-[0.25em] text-[#FF6600] font-semibold">{title}</div>
      {children}
    </section>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-[#002060]/50 font-semibold">{label}</span>
      {children}
    </label>
  );
}
function Input({ value, onChange, placeholder, type = "text", inputMode, step, min, testid, align }) {
  return (
    <input
      type={type}
      inputMode={inputMode}
      step={step}
      min={min}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className={`mt-1 w-full h-9 px-2 text-sm rounded bg-[#FFFFFF] border border-[#002060]/20 focus:border-[#FF6600] focus:outline-none ${align === "right" ? "text-right num" : ""}`}
    />
  );
}
function Chip({ children, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-xs text-[#002060]/70 bg-[#FFFFFF] hover:bg-[#FF6600] hover:text-[#FFFFFF] hover:border-[#FF6600] border border-[#002060]/20 rounded-full px-2.5 py-1 transition"
    >{children}</button>
  );
}
function Toggle({ checked, onChange, testid, label, tail, suffix }) {
  return (
    <label className="flex items-center justify-between gap-2 cursor-pointer select-none py-1.5 text-sm text-[#002060]/80">
      <span className="inline-flex items-center gap-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testid}
          className="h-4 w-4 rounded border-[#002060]/30 text-[#FF6600] focus:ring-orange"
        />
        {label}
        {suffix}
      </span>
      {tail}
    </label>
  );
}
function TotalRow({ k, v, tone = "default" }) {
  const tones = { default: "text-[#FFFFFF]", emerald: "text-[#FF6600]" };
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#FFFFFF]/70">{k}</span>
      <span className={`num ${tones[tone]}`}>
        {Number(v) < 0 ? "−$" : "$"}{Math.abs(Number(v)).toFixed(2)}
      </span>
    </div>
  );
}
function SendChip({ active, disabled, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`h-9 rounded text-xs font-semibold border transition ${
        active
          ? "bg-[#002060] text-[#FFFFFF] border-[#002060]"
          : "bg-[#FFFFFF] text-[#002060]/70 border-[#002060]/20 hover:border-[#FF6600]"
      } ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
    >{children}</button>
  );
}

// ============== Helpers ==============
function seedItems(job) {
  if (!job) return [{ description: "", qty: 1, unit_price: "", kind: "labor" }];
  const list = [{ description: "Diagnostic visit", qty: 1, unit_price: 89, kind: "labor" }];
  const price = job.final_price ?? job.quoted_price;
  if (price && price > 89) {
    list.push({
      description: `Labor — ${job.appliance || "appliance"} repair`,
      qty: 1,
      unit_price: Number(price) - 89,
      kind: "labor",
    });
  } else {
    list.push({
      description: `Labor — ${job.appliance || "appliance"} repair`,
      qty: 1,
      unit_price: "",
      kind: "labor",
    });
  }
  return list;
}
